'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import { Alert } from '@//components/Alert';
import { BulletList, BulletListItem } from '@//components/BulletList';
import { Button } from '@//components/Button';
import { InputSearch } from '@//components/InputSearch';
import { VStack } from '@//components/VStack';
import { useLegacyTrackEquipmentFilterSearch } from '@//data/tracking/useLegacyTrackEquipmentFilterSearch';
import { useLegacyTrackOptionsClick } from '@//data/tracking/useLegacyTrackOptionsClick';
import { useTriggerOnce } from '@//hooks/useTriggerOnce';
import { normalizeForSearch } from '@//utils/text';

const EQUIPMENT_PREVIEW_LIMIT = 16;
const EQUIPMENT_LIST_ID = 'equipment-list';

interface EquipmentListProps {
  vehicle: ElasticVehicle;
  options: string[];
}

export const EquipmentList = ({ vehicle, options }: EquipmentListProps) => {
  const t = useTranslations('pages.item.equipmentSection');
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState(false);

  const { trackLegacyEquipmentFilterSearch } = useLegacyTrackEquipmentFilterSearch();
  const { trackLegacyOptionsClick } = useLegacyTrackOptionsClick();
  const trackOptionsClickOnce = useTriggerOnce(() => trackLegacyOptionsClick(vehicle));

  const isSearching = searchTerm.trim() !== '';

  const visibility = useMemo(() => {
    const normalizedTerm = normalizeForSearch(searchTerm);
    return options.map((option, index) => {
      if (isSearching) {
        return normalizeForSearch(option).includes(normalizedTerm);
      }
      return expanded || index < EQUIPMENT_PREVIEW_LIMIT;
    });
  }, [options, isSearching, searchTerm, expanded]);

  const hasMatches = visibility.some(Boolean);
  const showNoResults = isSearching && !hasMatches;
  const showToggle = options.length > EQUIPMENT_PREVIEW_LIMIT && !isSearching;

  useEffect(() => {
    if (!isSearching) return;
    const timeoutId = setTimeout(() => {
      trackLegacyEquipmentFilterSearch({ vehicle, search: searchTerm });
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, isSearching, vehicle, trackLegacyEquipmentFilterSearch]);

  const handleToggle = () => {
    if (!expanded) trackOptionsClickOnce();
    setExpanded((prev) => !prev);
  };

  return (
    <VStack gap={24}>
      <InputSearch
        id="equipment-search"
        label={t('searchBar.label')}
        placeholder={t('searchBar.placeholder')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      {showNoResults ? (
        <Alert variant="info" id="alert-no-equipment-results">
          {t('noResults')}
        </Alert>
      ) : (
        <BulletList testId={EQUIPMENT_LIST_ID} columnLayout>
          {options.map((option, index) => (
            <BulletListItem
              key={index}
              testId={`equipment-list-item-${index + 1}`}
              hidden={!visibility[index]}
            >
              {option}
            </BulletListItem>
          ))}
        </BulletList>
      )}
      {showToggle && (
        <Button
          size="lg"
          variant="secondary"
          fullWidthLtMd
          onClick={handleToggle}
          title={expanded ? t('showLessButton.title') : t('showAllButton.title')}
          aria-expanded={expanded}
          aria-controls={EQUIPMENT_LIST_ID}
        >
          {expanded ? t('showLessButton.label') : t('showAllButton.label')}
        </Button>
      )}
    </VStack>
  );
};
